import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Lock,
  LogOut,
  Users,
  Home,
  FileText,
  ShoppingBag,
  Receipt,
  Package as PackageIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import BrandMark from '../components/BrandMark';
import { loadWaivers } from '../lib/waiverStorage';
import { loadSales, loadPackages } from '../lib/salesStorage';
import WaiversTab from './admin/WaiversTab';
import POSTab from './admin/POSTab';
import SalesTab from './admin/SalesTab';
import PackagesTab from './admin/PackagesTab';
import ClientDetailModal from './admin/ClientDetailModal';

const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'cryo2024';
const SESSION_KEY = 'hbc_admin_session';

const TABS = [
  { id: 'waivers', label: 'Waivers', icon: FileText },
  { id: 'pos', label: 'New Sale', icon: ShoppingBag },
  { id: 'sales', label: 'Sales', icon: Receipt },
  { id: 'packages', label: 'Packages', icon: PackageIcon },
];

export default function Admin() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(
    sessionStorage.getItem(SESSION_KEY) === '1'
  );
  const [password, setPassword] = useState('');
  const [pwError, setPwError] = useState(false);
  const [activeTab, setActiveTab] = useState('waivers');

  const [waivers, setWaivers] = useState([]);
  const [sales, setSales] = useState([]);
  const [packages, setPackages] = useState([]);
  const [selectedWaiver, setSelectedWaiver] = useState(null);

  const refreshAll = () => {
    setWaivers(loadWaivers());
    setSales(loadSales());
    setPackages(loadPackages());
  };

  useEffect(() => {
    if (authed) refreshAll();
  }, [authed]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      sessionStorage.setItem(SESSION_KEY, '1');
      setAuthed(true);
      setPwError(false);
      setPassword('');
      toast.success('Signed in as staff.');
    } else {
      setPwError(true);
      toast.error('Incorrect password.');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setAuthed(false);
    toast('Signed out.');
  };

  if (!authed) {
    return (
      <div className="min-h-screen frost-bg relative">
        <div className="relative z-10 min-h-screen flex items-center justify-center px-6 py-10">
          <div className="w-full max-w-md glass-card rounded-[2rem] p-8 sm:p-10 reveal">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center mb-6">
                <Lock className="w-7 h-7 text-blue-500" strokeWidth={1.5} />
              </div>
              <BrandMark size="sm" showAddress={false} />
              <h1 className="mt-6 font-display text-3xl text-slate-100 tracking-tight">
                Staff Access
              </h1>
              <p className="mt-2 text-sm text-slate-300">
                Enter the admin password to view signed waivers.
              </p>
            </div>

            <form onSubmit={handleLogin} className="mt-8">
              <label className="text-xs tracking-[0.2em] uppercase text-blue-300 font-bold mb-2 block">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPwError(false);
                }}
                placeholder="Enter password"
                className={`glass-input w-full h-16 rounded-2xl px-6 text-lg text-slate-100 placeholder:text-slate-400 ${
                  pwError ? 'border-red-400 focus:border-red-500' : ''
                }`}
                data-testid="admin-password-input"
              />
              {pwError && (
                <p className="mt-2 text-sm text-red-600" data-testid="admin-password-error">
                  Incorrect password. Please try again.
                </p>
              )}
              <button
                type="submit"
                className="pill-btn w-full mt-6 h-16 rounded-full bg-blue-500 hover:bg-blue-500 text-white text-lg font-bold shadow-xl shadow-blue-500/30"
                data-testid="admin-login-button"
              >
                Unlock
              </button>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="w-full mt-3 h-12 rounded-full text-blue-300 hover:text-blue-200 text-sm font-semibold"
                data-testid="admin-back-home-button"
              >
                Back to Home
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  const activePackagesCount = packages.filter(p => p.sessionsRemaining > 0).length;

  return (
    <div className="min-h-screen bg-slate-950">
      <header className="bg-white border-b border-white/10 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="pill-btn inline-flex items-center gap-2 h-11 px-4 rounded-full bg-slate-900 hover:bg-slate-800 text-slate-200 text-sm font-semibold"
              data-testid="admin-home-button"
            >
              <Home className="w-4 h-4" />
              Home
            </button>
            <div className="hidden sm:block h-6 w-px bg-slate-800" />
            <div className="font-display text-2xl text-slate-100 tracking-tight">
              Staff Dashboard
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="hidden sm:flex items-center gap-2 px-3 h-11 rounded-full bg-blue-500/10 text-blue-300 border border-blue-500/20 text-sm font-semibold">
              <Users className="w-4 h-4" />
              {waivers.length} clients
            </div>
            <button
              onClick={handleLogout}
              className="pill-btn inline-flex items-center gap-2 h-11 px-4 rounded-full bg-slate-900 hover:bg-slate-800 text-slate-200 text-sm font-semibold"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="flex gap-1 -mb-px overflow-x-auto">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = activeTab === t.id;
              const badge =
                t.id === 'waivers' ? waivers.length :
                t.id === 'sales' ? sales.length :
                t.id === 'packages' ? activePackagesCount :
                null;
              return (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  className={`inline-flex items-center gap-2 px-5 h-12 border-b-2 text-sm font-semibold transition-colors whitespace-nowrap ${
                    active
                      ? 'border-blue-500 text-blue-300'
                      : 'border-transparent text-slate-400 hover:text-slate-100'
                  }`}
                  data-testid={`admin-tab-${t.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {t.label}
                  {badge !== null && badge > 0 && (
                    <span className={`ml-1 rounded-full px-2 py-0.5 text-xs font-bold ${
                      active
                        ? 'bg-blue-500/20 text-blue-300'
                        : 'bg-slate-900 text-slate-300'
                    }`}>
                      {badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        {activeTab === 'waivers' && (
          <WaiversTab
            waivers={waivers}
            onChange={refreshAll}
            onView={(w) => setSelectedWaiver(w)}
          />
        )}
        {activeTab === 'pos' && (
          <POSTab waivers={waivers} onSaleCreated={refreshAll} />
        )}
        {activeTab === 'sales' && (
          <SalesTab sales={sales} onChange={refreshAll} />
        )}
        {activeTab === 'packages' && (
          <PackagesTab packages={packages} onChange={refreshAll} />
        )}
      </main>

      {selectedWaiver && (
        <ClientDetailModal
          waiver={selectedWaiver}
          onClose={() => setSelectedWaiver(null)}
        />
      )}

      <button
        onClick={() => navigate('/')}
        className="fixed bottom-6 left-6 pill-btn hidden md:inline-flex items-center gap-2 h-11 px-4 rounded-full bg-white border border-white/10 hover:bg-slate-950 text-slate-200 text-sm font-semibold shadow-md"
        data-testid="admin-back-button"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Kiosk
      </button>
    </div>
  );
}
